import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ArrowLeft,
  Heart,
  Check,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import "./AvailabilityRequestsPage.css";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import pawPrintIcon from "../../assets/pawIcon.svg";
import apiClient from "../../api/apiClient";
import TopNavBar from "../../components/TopNavBar";
import userPlaceholder from "../../assets/userPlaceholder.png";
import appIcon from "../../assets/socialPawsLogo.svg";
import MobileTopBar from "../../components/MobileTopBar";
import morningIconSvg from "../../assets/morning.svg";
import afternoonIconSvg from "../../assets/afternoon.svg";
import eveningIconSvg from "../../assets/evening.svg";

dayjs.extend(isSameOrAfter);

const AvailabilityRequestsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useSelector((state) => state.user);

  const [user, setUser] = useState(null);
  const [processedRequests, setProcessedRequests] = useState({});
  const [availabilityGrid, setAvailabilityGrid] = useState({});
  const [selections, setSelections] = useState({});
  const [lockedIds, setLockedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const bookingData = location.state || {};
  const partnerData = bookingData.partnerData || null;
  const partnerId = bookingData.partnerId || null;
  const dogName = bookingData.myDogName || "My Dog";
  const partnerName = bookingData.partnerName || "Partner Name";
  const bookingId = bookingData.bookingId || null;
  const selectedDateStr = bookingData.selectedDate;

  const [targetDate, setTargetDate] = useState(
    selectedDateStr ? dayjs(selectedDateStr) : dayjs(),
  );

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const res = await apiClient.get(`/user/details/${profile?.id}`);
        if (res.data?.status && res.data.data) setUser(res.data.data);
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    if (profile?.id) fetchUserDetails();
  }, [profile?.id]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const targetWeekEndStr = targetDate.endOf("week").format("YYYY-MM-DD");

      const [requestsRes, approvedRes] = await Promise.all([
        apiClient.get(`/bookingWeeks/getBookingWeeksByUserId/${profile?.id}`),
        apiClient.get(
          `/bookingWeeks/get-Approved-Booking-WeeksBy-UserId/${profile?.id}`,
        ),
      ]);

      const allData = requestsRes.data?.data || [];
      const approvedData = approvedRes.data?.data || [];

      const validLockedIds = [];
      const autoSelections = {};

      // Helper to strictly read boolean values from API
      const isSlotActive = (val) => val === true || val === "true" || val === 1;

      approvedData.forEach((booking) => {
        const weekEndStr = dayjs(booking.week_end).format("YYYY-MM-DD");
        if (dayjs(weekEndStr).isSameOrAfter(targetWeekEndStr, "day")) {
          booking.days.forEach((day) => {
            if (day.is_approved) {
              validLockedIds.push(day.id);
              const timesMap = [
                { key: "morning", suffix: "morning" },
                { key: "afternoon", suffix: "afternoon" },
                { key: "evening", suffix: "evening" },
                { key: "full_day", suffix: "full" },
              ];
              timesMap.forEach(({ key, suffix }) => {
                if (isSlotActive(day[key])) {
                  autoSelections[`${day.day_of_week}-${suffix}`] = day.id;
                }
              });
            }
          });
        }
      });

      setLockedIds(validLockedIds);
      setSelections(autoSelections);

      const rawData = allData.filter((item) => {
        if (!item.week_end) return false;
        const itemWeekEndStr = dayjs(item.week_end).format("YYYY-MM-DD");
        return itemWeekEndStr === targetWeekEndStr;
      });

      const gridMap = {};
      const groupedData = {};

      const getPartnerId = (item) =>
        item.requester_user_id === profile?.id
          ? item.walker_user_id
          : item.requester_user_id;

      const uniquePartnerIds = [...new Set(rawData.map(getPartnerId))];

      const partnerProfiles = {};
      await Promise.all(
        uniquePartnerIds.map(async (id) => {
          try {
            const wRes = await apiClient.get(`/user/details/${id}`);
            if (wRes.data?.data) partnerProfiles[id] = wRes.data.data;
          } catch (e) {
            console.error(e);
          }
        }),
      );

      rawData.forEach((booking) => {
        const partnerId = getPartnerId(booking);
        const partner = partnerProfiles[partnerId];

        booking.days.forEach((day) => {
          const dayName = day.day_of_week;
          const timesMap = [
            { key: "morning", suffix: "morning" },
            { key: "afternoon", suffix: "afternoon" },
            { key: "evening", suffix: "evening" },
            { key: "full_day", suffix: "full" },
          ];

          timesMap.forEach(({ key, suffix }) => {
            if (isSlotActive(day[key])) {
              const slotKey = `${dayName}-${suffix}`;
              gridMap[slotKey] = true;
              if (!groupedData[slotKey]) {
                groupedData[slotKey] = { dateLabel: day.date, applicants: [] };
              }
              groupedData[slotKey].applicants.push({
                booking_day_id: day.id,
                name: partner?.user_name || "Unknown",
                img: partner?.user_profile_image?.[0] || partner?.dogs?.[0]?.profile_image?.[0] || userPlaceholder,
                location: partner?.user_city || "Nearby",
                rating: 5.0,
              });
            }
          });
        });
      });

      setAvailabilityGrid(gridMap);
      setProcessedRequests(groupedData);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [profile?.id, targetDate]);

  useEffect(() => {
    if (profile?.id) {
      fetchAllData();
    }
  }, [profile?.id, fetchAllData, targetDate]);

  const isSendDisabled = useMemo(() => {
    const currentSelectedIds = Object.values(selections).filter(
      (id) => id !== null,
    );
    const newSelections = currentSelectedIds.filter(
      (id) => !lockedIds.includes(id),
    );
    return newSelections.length === 0 || submitting;
  }, [selections, lockedIds, submitting]);

  // Handle right side applicant card click
  const handleSelectApplicant = (slotKey, dayId) => {
    if (lockedIds.includes(dayId) || lockedIds.includes(selections[slotKey])) {
      return;
    }

    setSelections((prev) => {
      const isCurrentlySelected = prev[slotKey] === dayId;
      const newValue = isCurrentlySelected ? null : dayId;
      const newSelections = { ...prev, [slotKey]: newValue };

      const [dayName, slotType] = slotKey.split("-");

      // Cascade full-day selection to morning, afternoon, evening
      if (slotType === "full") {
        ["morning", "afternoon", "evening"].forEach((time) => {
          const subKey = `${dayName}-${time}`;
          if (!lockedIds.includes(prev[subKey]) && !lockedIds.includes(dayId)) {
            newSelections[subKey] = newValue;
          }
        });
      }

      return newSelections;
    });
  };

  // Handle left side grid cell click
  const handleCellClick = (slotKey) => {
    const data = processedRequests[slotKey];
    if (!data || data.applicants.length === 0) return;

    const currentSelection = selections[slotKey];
    let newValue = null;

    if (currentSelection) {
      if (!lockedIds.includes(currentSelection)) {
        newValue = null;
      } else {
        return; // Locked slot
      }
    } else {
      const firstAvailable = data.applicants.find(
        (app) => !lockedIds.includes(app.booking_day_id),
      );
      if (firstAvailable) {
        newValue = firstAvailable.booking_day_id;
      } else {
        return; // No available applicants
      }
    }

    setSelections((prev) => {
      const newSelections = { ...prev, [slotKey]: newValue };
      const [dayName, slotType] = slotKey.split("-");

      // Cascade full-day selection to morning, afternoon, evening
      if (slotType === "full") {
        ["morning", "afternoon", "evening"].forEach((time) => {
          const subKey = `${dayName}-${time}`;
          if (!lockedIds.includes(prev[subKey])) {
            newSelections[subKey] = newValue;
          }
        });
      }

      return newSelections;
    });
  };

  const handleSendApproval = async () => {
    const currentSelectedIds = Object.values(selections).filter(
      (id) => id !== null,
    );
    // Extract unique IDs
    const uniqueIds = [...new Set(currentSelectedIds)];
    const onlyNewIds = uniqueIds.filter((id) => !lockedIds.includes(id));

    setSubmitting(true);
    try {
      await apiClient.put("/bookingWeeks/update-booking-daysApproval-byId", {
        booking_day_id: onlyNewIds,
        is_approved: true,
      });
      fetchAllData();
      navigate("/team-paws-availability", {
        state: {
          partnerId,
          dogName,
          partnerName: partnerName,
          bookingId: bookingId,
        },
      });
    } catch (error) {
      alert("Failed to update approval.");
    } finally {
      setSubmitting(false);
    }
  };

  const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

  const renderIcon = (type, size) => {
    const iconMap = {
      morning: morningIconSvg,
      afternoon: afternoonIconSvg,
      evening: eveningIconSvg,
    };
    return (
      <img
        src={iconMap[type]}
        alt={type}
        style={{ width: size, height: size }}
      />
    );
  };

  const timeSlotsUI = [
    {
      id: "morning",
      label: "MORNING",
      sub: "Before 12 noon",
      icon: renderIcon("morning", 20),
    },
    {
      id: "afternoon",
      label: "AFTERNOON",
      sub: "12 noon - 5 pm",
      icon: renderIcon("afternoon", 20),
    },
    {
      id: "evening",
      label: "EVENING",
      sub: "After 5 pm",
      icon: renderIcon("evening", 20),
    },
    { id: "full", label: "FULL DAY", sub: "+ Sleepover", icon: null },
  ];

  const renderSmallSlot = (hasData, count, type) => {
    let cellClass = "arp-small-grid-cell";
    let iconSize = 20;

    if (hasData) {
      cellClass += " arp-small-cell-booked";
    } else {
      cellClass += " arp-small-cell-unselected";
    }

    let content = null;
    if (type === "morning") {
      content = (
        <img
          src={morningIconSvg}
          alt={type}
          className="arp-small-icon"
          style={{ width: iconSize, height: iconSize }}
        />
      );
    } else if (type === "afternoon") {
      content = (
        <img
          src={afternoonIconSvg}
          alt={type}
          className="arp-small-icon"
          style={{ width: iconSize, height: iconSize }}
        />
      );
    } else if (type === "evening") {
      content = (
        <img
          src={eveningIconSvg}
          alt={type}
          className="arp-small-icon"
          style={{ width: iconSize, height: iconSize }}
        />
      );
    }

    return (
      <div className={cellClass}>
        {content}
        {hasData && count > 0 && <div className="arp-small-badge">{count}</div>}
      </div>
    );
  };

  const monthsList = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const yearsList = Array.from(
    { length: 10 },
    (_, i) => dayjs().year() - 2 + i,
  );

  const handleMonthChange = (month) => {
    setTargetDate((prev) => prev.month(Number(month)));
  };

  const handleYearChange = (year) => {
    setTargetDate((prev) => prev.year(Number(year)));
  };

  const prevWeek = () => {
    setTargetDate((prev) => prev.subtract(1, "week"));
  };

  const nextWeek = () => {
    setTargetDate((prev) => prev.add(1, "week"));
  };

  if (loading)
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(157.14deg, #96f2de 2.76%, #58b8a3 96.76%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} />
      </div>
    );

  return (
    <div className="arp-container">
      {/* DESKTOP HEADER */}
      <div className="cb-custom-top-header cb-desktop-only">
        <div className="cb-custom-brand" onClick={() => navigate(-1)}>
          <img
            src={appIcon}
            alt="Social Paws"
            style={{ width: "32px", height: "32px", objectFit: "contain" }}
          />
          <span>Social Paws</span>
        </div>
        <div className="header-icons-right">
          <TopNavBar theme="light" />
        </div>
      </div>

      {/* MOBILE HEADER */}
      <div
        className="cb-custom-top-header cb-mobile-only"
        style={{ background: "transparent", marginBottom: "-2rem" }}
      >
        <MobileTopBar
          onBack={() => navigate(-1)}
          backColor="#000"
          logoColor="#009fd9"
          blueLogo="true"
        />

        <div className="cb-header-bottom-row">
          <div className="header-icons-right">
            <TopNavBar theme="light" />
          </div>
        </div>
      </div>

      <div className="tpa-scroll-wrapper">
        <div className="arp-content">
          <h1 className="arp-title">
            {user?.dogs?.[0]?.dog_name?.toUpperCase() || dogName.toUpperCase()}
            'S AVAILABILITY
          </h1>
          <p className="arp-subtitle">Approve requests here</p>

          <div className="arp-top-controls">
            <div className="arp-dropdown-container">
              <select
                value={targetDate.month()}
                onChange={(e) => handleMonthChange(e.target.value)}
              >
                {monthsList.map((m, i) => (
                  <option key={m} value={i}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div className="arp-dropdown-container">
              <select
                value={targetDate.year()}
                onChange={(e) => handleYearChange(e.target.value)}
              >
                {yearsList.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="arp-split-layout">
            {/* LEFT COLUMN: Weekly Planner */}
            <div className="arp-left-col arp-planner-container">
              <h3 className="arp-planner-title">Weekly-Planner</h3>

              <div className="arp-week-nav">
                <button onClick={prevWeek}>
                  <ChevronLeft size={20} />
                </button>
                <span>
                  {targetDate.startOf("week").format("ddd MMM D")} -{" "}
                  {targetDate.endOf("week").format("ddd MMM D")}
                </span>
                <button onClick={nextWeek}>
                  <ChevronRight size={20} />
                </button>
              </div>
              <div className="arp-planner-subtitle">Select Slots here</div>

              <div className="arp-grid-header">
                <div className="arp-empty-cell"></div>
                {timeSlotsUI.map((slot) => (
                  <div key={slot.id} className="arp-header-label">
                    <span
                      className={
                        slot.id === "full"
                          ? "arp-slot-title-last"
                          : "arp-slot-title"
                      }
                    >
                      {slot.label}
                    </span>
                    <span
                      className={
                        slot.id === "full"
                          ? "arp-slot-sub-last"
                          : "arp-slot-sub"
                      }
                    >
                      {slot.sub}
                    </span>
                  </div>
                ))}
              </div>

              <div className="arp-grid-body">
                {days.map((day, index) => {
                  const dateForDay = targetDate
                    .startOf("week")
                    .add(index, "day");
                  return (
                    <div key={day} className="arp-grid-row">
                      <div className="arp-day-label">
                        {day}, {dateForDay.format("DD")}
                      </div>

                      {["morning", "afternoon", "evening"].map((slot) => {
                        const slotKey = `${day}-${slot}`;
                        const hasData = availabilityGrid[slotKey];
                        const isSelected = !!selections[slotKey];
                        const dataCount =
                          processedRequests[slotKey]?.applicants?.length || 0;

                        let cellClass = "arp-grid-cell";
                        if (hasData) {
                          cellClass += " arp-cell-has-data";
                          if (isSelected) cellClass += " arp-cell-selected";
                        } else {
                          cellClass += " arp-cell-no-data";
                        }

                        return (
                          <div
                            key={slot}
                            className={cellClass}
                            onClick={() => handleCellClick(slotKey)}
                            style={{ position: "relative" }}
                          >
                            {slot === "morning" && (
                              <img
                                src={morningIconSvg}
                                alt="Morning"
                                className="arp-cell-icon"
                                style={{ width: 30, height: 30 }}
                              />
                            )}
                            {slot === "afternoon" && (
                              <img
                                src={afternoonIconSvg}
                                alt="Afternoon"
                                className="arp-cell-icon"
                                style={{ width: 30, height: 30 }}
                              />
                            )}
                            {slot === "evening" && (
                              <img
                                src={eveningIconSvg}
                                alt="Evening"
                                className="arp-cell-icon"
                                style={{ width: 30, height: 30 }}
                              />
                            )}

                            {dataCount > 0 && (
                              <div className="arp-badge">{dataCount}</div>
                            )}
                          </div>
                        );
                      })}

                      {/* FULL DAY Interactive Checkbox / Grid Cell */}
                      {(() => {
                        const fullSlotKey = `${day}-full`;
                        const hasFullData = availabilityGrid[fullSlotKey];
                        const isFullSelected = !!selections[fullSlotKey];
                        const fullDataCount =
                          processedRequests[fullSlotKey]?.applicants?.length ||
                          0;

                        return (
                          <div
                            className={`arp-checkbox ${
                              hasFullData
                                ? "arp-cell-has-data"
                                : "arp-cell-no-data"
                            } ${isFullSelected ? "arp-cell-selected" : ""}`}
                            className={`arp-checkbox ${"arp-cell-no-data"} ${isFullSelected ? "arp-cell-selected" : ""}`}
                            onClick={() => handleCellClick(fullSlotKey)}
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: hasFullData ? "pointer" : "default",
                            }}
                          >
                            {hasFullData && <Check size={20} />}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>

              {/* Visual Bottom Bulk Selectors */}
              <div className="arp-bulk-select-row">
                <div className="arp-bulk-item">
                  <span>SELECT ALL</span>
                  <div className="arp-bulk-checkbox"></div>
                </div>
                <div className="arp-bulk-item">
                  <span>WEEKDAYS</span>
                  <div className="arp-bulk-checkbox"></div>
                </div>
                <div className="arp-bulk-item">
                  <span>WEEKENDS</span>
                  <div className="arp-bulk-checkbox"></div>
                </div>
              </div>
            </div>

            <div className="arp-divider"></div>

            {/* RIGHT COLUMN: Approve Requests */}
            <div className="arp-right-col">
              <div className="arp-active-slots-header">
                <h3 className="arp-section-label">Active Slots</h3>
                <div className="arp-active-month-chip">
                  {targetDate.format("MMM YYYY")}
                </div>
              </div>

              {Object.keys(processedRequests).map((slotKey) => {
                const data = processedRequests[slotKey];
                const [dayName, slotName] = slotKey.split("-");
                const dateObj = dayjs(data.dateLabel);

                const mData = processedRequests[`${dayName}-morning`];
                const aData = processedRequests[`${dayName}-afternoon`];
                const eData = processedRequests[`${dayName}-evening`];
                // const fData = processedRequests[`${dayName}-full`];

                const displaySlotName =
                  slotName === "full"
                    ? "Full Day"
                    : slotName.charAt(0).toUpperCase() + slotName.slice(1);

                return (
                  <div key={slotKey} className="arp-request-group">
                    <div className="arp-request-header-row">
                      <div className="arp-active-day-card">
                        <div className="arp-active-day-title">
                          {dayName.charAt(0).toUpperCase() +
                            dayName.slice(1).toLowerCase()}
                          , {dateObj.format("DD")}
                        </div>
                        <div className="arp-active-day-slots">
                          {renderSmallSlot(
                            !!mData,
                            mData?.applicants?.length || 0,
                            "morning",
                          )}
                          {renderSmallSlot(
                            !!aData,
                            aData?.applicants?.length || 0,
                            "afternoon",
                          )}
                          {renderSmallSlot(
                            !!eData,
                            eData?.applicants?.length || 0,
                            "evening",
                          )}
                          {/* {renderSmallSlot(
                            !!fData,
                            fData?.applicants?.length || 0,
                            "full",
                          )} */}
                        </div>
                      </div>
                      <h3 className="arp-request-slot-name">
                        {displaySlotName}
                      </h3>
                    </div>

                    <div className="arp-applicants-row">
                      {data.applicants.map((applicant) => {
                        const isSelected =
                          selections[slotKey] === applicant.booking_day_id;
                        const isLocked = lockedIds.includes(
                          applicant.booking_day_id,
                        );

                        return (
                          <div
                            key={applicant.booking_day_id}
                            className={`arp-applicant-wrapper ${
                              isLocked ? "locked-applicant" : ""
                            }`}
                          >
                            <div
                              className={`arp-radio ${
                                isSelected ? "selected" : ""
                              } ${isLocked ? "locked-radio" : ""}`}
                              onClick={() =>
                                handleSelectApplicant(
                                  slotKey,
                                  applicant.booking_day_id,
                                )
                              }
                            >
                              {isLocked ? (
                                <div className="arp-radio-dot"></div>
                              ) : (
                                isSelected && (
                                  <div className="arp-radio-dot"></div>
                                )
                              )}
                            </div>

                            <div
                              className={`arp-applicant-card ${
                                isSelected ? "card-selected" : ""
                              } ${isLocked ? "card-locked" : ""}`}
                              onClick={() =>
                                handleSelectApplicant(
                                  slotKey,
                                  applicant.booking_day_id,
                                )
                              }
                            >
                              <img
                                src={applicant.img}
                                alt={applicant.name}
                                className="arp-app-img"
                              />
                              <div className="arp-app-overlay">
                                <div className="arp-app-info">
                                  <div className="arp-app-name-row">
                                    <Heart size={14} />{" "}
                                    <span>{applicant.name}</span>
                                  </div>
                                  <div className="arp-app-meta-row">
                                    <div className="arp-meta-badge">
                                      <img
                                        src={pawPrintIcon}
                                        alt="Icon"
                                        style={{ width: "16px" }}
                                      />
                                      <span>{applicant.location}</span>
                                    </div>
                                    <div className="arp-meta-badge">
                                      <img
                                        src={pawPrintIcon}
                                        alt="Icon"
                                        style={{ width: "16px" }}
                                      />
                                      <span>{applicant.rating}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="arp-footer">
            <button
              className={`arp-send-btn ${isSendDisabled ? "btn-disabled" : ""}`}
              onClick={handleSendApproval}
              disabled={isSendDisabled}
            >
              {submitting ? "SENDING..." : "SEND APPROVAL"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityRequestsPage;
